/**
 * File Constants
 *
 * Centralized constants for file handling across all applications.
 * Includes file type validation, size limits, and MIME types.
 *
 * Used by:
 * - apps/api (upload validation)
 * - apps/duckdb-api (file processing)
 * - apps/app (frontend file validation)
 *
 * @module @loader/shared-types/constants/files
 */

// ============================================================================
// File Types
// ============================================================================

/**
 * Allowed file extensions for uploads
 *
 * Supported formats:
 * - CSV (.csv) - Comma-separated values
 * - JSON (.json) - JavaScript Object Notation
 * - Excel (.xlsx, .xls) - Microsoft Excel spreadsheets
 * - Parquet (.parquet) - Apache Parquet columnar storage
 */
export const ALLOWED_FILE_EXTENSIONS = [
  'csv',
  'json',
  'xlsx',
  'xls',
  'parquet',
] as const;

export type AllowedFileExtension = (typeof ALLOWED_FILE_EXTENSIONS)[number];

/**
 * Allowed MIME types for uploads
 *
 * Maps to ALLOWED_FILE_EXTENSIONS with proper content types.
 * Note: Some formats have multiple valid MIME types.
 */
export const ALLOWED_MIME_TYPES = [
  'text/csv',
  'application/csv',
  'application/json',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'application/octet-stream', // .parquet (often served as binary)
] as const;

export type AllowedMimeType = (typeof ALLOWED_MIME_TYPES)[number];

// ============================================================================
// File Size Limits
// ============================================================================

/**
 * Maximum file size for user uploads (100MB)
 *
 * This is the user-facing limit enforced by:
 * - apps/api (upload endpoint validation)
 * - apps/app (frontend file picker validation)
 *
 * Constraints considered:
 * - CF Worker memory limits
 * - User experience (upload time)
 * - Reasonable data set sizes
 */
export const MAX_UPLOAD_FILE_SIZE = 100 * 1024 * 1024;

/**
 * Maximum file size for DuckDB processing (500MB)
 *
 * This is the internal limit for file processing operations.
 * Higher than user upload limit to allow for:
 * - Parquet file expansion during processing
 * - Internal data transformations
 * - Temporary file operations
 *
 * Used by:
 * - apps/duckdb-api (file processing routes)
 */
export const MAX_PROCESSING_FILE_SIZE = 500 * 1024 * 1024;

// ============================================================================
// File Processing Constants
// ============================================================================

/**
 * Maximum number of sample values to show in schema preview
 *
 * Used when displaying field value samples during schema extraction.
 */
export const MAX_SAMPLE_VALUES = 5;

/**
 * Maximum number of preview rows to display
 *
 * Used for schema extraction and data preview.
 */
export const MAX_PREVIEW_ROWS = 100;

/**
 * Maximum number of validation errors to return
 *
 * Limits the number of error samples returned to prevent
 * overwhelming the client with error messages.
 */
export const MAX_VALIDATION_ERRORS = 100;

/**
 * Maximum rows to read from temp table during processing
 *
 * Safety limit for temporary table operations.
 */
export const MAX_TEMP_TABLE_ROWS = 100_000;

/**
 * Maximum rows sampled for type inference (numeric/temporal detection)
 *
 * Expensive per-row operations like TRY_STRPTIME and TRY_CAST run on this
 * many rows. Keeps analysis fast while providing enough data for reliable
 * type detection at the 80% threshold.
 */
export const TYPE_INFERENCE_SAMPLE_LIMIT = 500;
